import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { MSection, SectionHeader } from '@/components/marketing/section'

export const Route = createFileRoute('/_site/integrations/slack')({
  component: SlackIntegrationPage,
  head: () => ({
    meta: [
      { title: 'Slack Uptime Monitoring & Downtime Alerts — Uptrack' },
      { name: 'description', content: 'Get instant Slack downtime alerts when your site goes down. Connect Uptrack to any Slack channel in 30 seconds with OAuth. Included with every plan.' },
      { property: 'og:title', content: 'Slack Uptime Monitoring & Downtime Alerts — Uptrack' },
      { property: 'og:description', content: 'Instant Slack alerts when your site goes down. OAuth setup in 30 seconds. Included with every plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/integrations-slack.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/integrations/slack' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://uptrack.app/integrations' },
            { '@type': 'ListItem', position: 3, name: 'Slack', item: 'https://uptrack.app/integrations/slack' },
          ],
        }),
      },
    ],
  }),
})

function SlackIntegrationPage() {
  return (
    <>
      {/* Hero */}
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Slack uptime monitoring & downtime alerts.
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              Your team already lives in Slack. Get downtime alerts where you work — instant notifications the moment a monitor fails, with context to act immediately.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">Available on every plan</p>
            <div className="mt-8">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>

      {/* Why */}
      <MSection tone="raised">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              title="Why Slack for monitoring alerts?"
              lead="Email alerts get buried. Slack alerts get seen. Your ops channel already has the people who need to respond — put alerts where they will actually be noticed."
            />
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-background p-6 font-mono text-sm">
              <p className="text-status-down font-semibold">[ALERT] api.example.com is DOWN</p>
              <p className="text-muted-foreground mt-1">Status: 503 | Response time: 12,340ms</p>
              <p className="text-muted-foreground">Failures: 3 | Started: 08:23 UTC</p>
              <p className="text-muted-foreground mt-2">Monitor: Production API | Started: 2 min ago</p>
            </div>
          </div>
        </div>
      </MSection>

      {/* How to set up */}
      <MSection>
        <SectionHeader title="How to set up Slack alerts" />
        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { step: '1', title: 'Connect with OAuth', desc: 'Click "Add to Slack" in your Uptrack alert channels. Authorize Uptrack to post to your workspace — no bot tokens to manage.' },
                { step: '2', title: 'Select a channel', desc: 'Pick any public or private channel. Most teams use #ops-alerts or #monitoring. You can add multiple channels for different monitors.' },
                { step: '3', title: 'Configure alert rules', desc: 'Choose which monitors send to Slack, set consecutive failure thresholds, and optionally enable recovery notifications.' },
          ].map((item) => (
            <li key={item.step} className="rounded-lg border border-border bg-card p-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background num text-sm font-semibold text-foreground">
                {item.step}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.desc}</p>
            </li>
          ))}
        </ol>
      </MSection>

      {/* What you get */}
      <MSection tone="raised">
        <SectionHeader title="What you get" />
        <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {[
            'Instant Slack messages on downtime and recovery',
                  'Rich alert format with status code, response time, and failure count',
                  'OAuth setup — no webhook URLs to copy',
                  'Multiple channels per workspace on paid plans',
                  'Per-monitor channel routing',
                  'Consecutive-check confirmation to reduce noise',
                  'Available on every plan (one chat channel on the free plan)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 border-t border-border pt-3 text-[15px] text-body">
              <span aria-hidden="true" className="mt-0.5 font-mono text-status-up">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </MSection>

      {/* CTA */}
      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="grid grid-cols-1 items-center gap-8 rounded-frame border border-border bg-card p-8 shadow-raised md:p-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Get Slack alerts in under a minute</h2>
              <p className="mt-3 max-w-xl text-lead text-body">
                Every plan includes 30-second checks and alert confirmation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
